/**
 * Card for a report.
 */
import * as React from 'react';
import { Calendar, User } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { Rating } from '@/components/forms/Rating';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableCard } from '@/components/common/data-table';
import { getRowPermission } from '@/hooks/usePageActions';
import type { SessionReport } from '@/types/inperson/report.types';
import { DataTableRowCheckbox } from '@/components/common/data-table/DataTableRowCheckbox';
import {
  deriveReportDisplay,
  formatReportSessionCardLabel,
  getReportActionState,
  resolveReportPermissions,
  type ReportPageActions,
} from './report-table-utils';

/** Props for {@link ReportCard}. */
interface ReportCardProps {
  report: SessionReport;
  onView?: (reportId: string) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onValidate?: () => void;
  pageActions?: ReportPageActions;
  isSelectable?: boolean;
  isSelected?: boolean;
  onToggleSelect?: () => void;
}

/** Card row for an in-person report in the list. */
export const ReportCard: React.FC<ReportCardProps> = ({
  report,
  onView,
  onEdit,
  onDelete,
  onValidate,
  pageActions,
  isSelectable = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const { canEdit: canEditResolved, canValidate } = resolveReportPermissions(
    report.session?.type ?? '',
    pageActions,
  );
  const canEdit = getRowPermission(report, 'action-edit-row', canEditResolved);
  const canDelete = getRowPermission(
    report,
    'action-delete-row',
    pageActions?.canDeleteRow ?? true,
  );

  const { statusConfig, StatusIcon, authorName, displayRating, establishmentName } =
    deriveReportDisplay(report);
  const sessionDisplayText = formatReportSessionCardLabel(report);
  const { editDisabled, editTooltip, validateDisabled, validateTooltip } =
    getReportActionState(report);

  return (
    <TableCard onClick={() => onView?.(report.id)}>
      <div className="data-card-header">
        <div className="flex items-center gap-2">
          {isSelectable && (
            <DataTableRowCheckbox checked={isSelected} onCheckedChange={() => onToggleSelect?.()} />
          )}
          <LabelBadge
            label={statusConfig.label}
            icon={StatusIcon}
            variant={statusConfig.badgeVariant}
            color={statusConfig.badgeColor}
            size="sm"
          />
        </div>
        {displayRating !== null && (
          <div className="flex items-center gap-1">
            <Rating value={displayRating} readOnly size="sm" showLabel={false} />
          </div>
        )}
      </div>

      <div className="data-card-content">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <Calendar className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-foreground text-sm">{sessionDisplayText}</span>
            {establishmentName && (
              <span className="text-xs text-muted-foreground">{establishmentName}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <span className="text-sm text-foreground">{authorName}</span>
        </div>
      </div>

      <div className="data-card-footer">
        <div className="flex gap-2">
          <Button
            preset="row-view"
            onClick={(e) => {
              e.stopPropagation();
              onView?.(report.id);
            }}
          />
          {canEdit && (
            <Button
              preset="row-edit"
              disabled={editDisabled}
              tooltipText={editTooltip}
              onClick={(e) => {
                e.stopPropagation();
                onEdit?.();
              }}
            />
          )}
          {canValidate && (
            <Button
              preset="row-validate"
              disabled={validateDisabled}
              tooltipText={validateTooltip}
              onClick={(e) => {
                e.stopPropagation();
                onValidate?.();
              }}
            />
          )}
          {canDelete && (
            <Button
              preset="row-delete"
              onClick={(e) => {
                e.stopPropagation();
                onDelete?.();
              }}
            />
          )}
        </div>
      </div>
    </TableCard>
  );
};
